import { useState } from 'react';
import { type UpsertSessionRequest, type TopicSession, type SessionType, type SessionStatus } from '@/lib/api/trainer';
import { DatePicker } from '@/components/ui/date-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const SESSION_TYPES: { value: SessionType; label: string; icon: string }[] = [
  { value: 'live_class', label: 'Live Class', icon: '🎥' },
  { value: 'recorded', label: 'Recorded', icon: '📹' },
  { value: 'office_hours', label: 'Office Hours', icon: '💬' },
  { value: 'lab', label: 'Lab / Workshop', icon: '🧪' },
];

const SESSION_STATUSES: { value: SessionStatus; label: string; color: string }[] = [
  { value: 'scheduled', label: 'Scheduled', color: 'border-[#ff5a1f] bg-[#fff1ec] text-[#ff5a1f] dark:bg-blue-950/30 dark:text-blue-400' },
  { value: 'live', label: 'Live now', color: 'border-green-400 bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400' },
  { value: 'completed', label: 'Completed', color: 'border-slate-400 bg-slate-50 text-slate-700 dark:bg-slate-800 dark:text-slate-400' },
  { value: 'cancelled', label: 'Cancelled', color: 'border-red-400 bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-400' },
];

const TIME_SLOTS = Array.from({ length: 33 }, (_, i) => {
  const mins = 360 + i * 30; // 06:00 → 22:00
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
});

function nextTimeSlot() {
  const now = new Date();
  const mins = Math.ceil((now.getHours() * 60 + now.getMinutes()) / 30) * 30;
  if (mins < 360 || mins > 1320) return '09:00';
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
}

function splitDateTime(iso: string): { date: string; time: string } {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-CA'); // YYYY-MM-DD
  const hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  // snap to nearest TIME_SLOT
  const time = TIME_SLOTS.includes(hhmm) ? hhmm : nextTimeSlot();
  return { date, time };
}

interface Props {
  initial?: TopicSession;
  onSave: (req: UpsertSessionRequest) => Promise<void>;
  onCancel: () => void;
}

const inputClass = 'w-full rounded-md border border-[#ececec] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
const labelClass = 'mb-1.5 block text-xs font-medium text-[#6b7280]';

export function SessionForm({ initial, onSave, onCancel }: Props) {
  const { date: initDate, time: initTime } = initial?.scheduled_at
    ? splitDateTime(initial.scheduled_at)
    : { date: '', time: nextTimeSlot() };

  const [sessionType, setSessionType] = useState<SessionType>(initial?.session_type ?? 'live_class');
  const [status, setStatus] = useState<SessionStatus>(initial?.status ?? 'scheduled');
  const [title, setTitle] = useState(initial?.title ?? '');
  const [scheduledDate, setScheduledDate] = useState(initDate);
  const [scheduledTime, setScheduledTime] = useState(initTime);
  const [durationMins, setDurationMins] = useState(initial?.duration_mins?.toString() ?? '');
  const [meetingUrl, setMeetingUrl] = useState(initial?.meeting_url ?? '');
  const [recordingUrl, setRecordingUrl] = useState(initial?.recording_url ?? '');
  const [attendanceRequired, setAttendanceRequired] = useState(initial ? (initial.attendance_required ?? false) : true);
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [busy, setBusy] = useState(false);

  const showMeetingUrl = sessionType === 'live_class' || sessionType === 'office_hours';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const scheduled_at =
        scheduledDate && scheduledTime
          ? new Date(`${scheduledDate}T${scheduledTime}:00`).toISOString()
          : undefined;
      await onSave({
        title,
        session_type: sessionType,
        status,
        scheduled_at,
        meeting_url: showMeetingUrl ? (meetingUrl || undefined) : undefined,
        recording_url: recordingUrl || undefined,
        duration_mins: durationMins ? parseInt(durationMins) : undefined,
        attendance_required: attendanceRequired || undefined,
        notes: notes.trim() || undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Session type picker */}
      <div>
        <label className={labelClass}>Session type *</label>
        <div className="grid grid-cols-2 gap-2">
          {SESSION_TYPES.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setSessionType(t.value)}
              className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                sessionType === t.value
                  ? 'border-orange-400 bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400'
                  : 'border-[#ececec] text-[#6b7280] hover:border-[#ececec]/80 hover:bg-[#fafafa]'
              }`}
            >
              <span>{t.icon}</span>
              <span className="font-medium">{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Status */}
      <div>
        <label className={labelClass}>Status</label>
        <div className="grid grid-cols-4 gap-2">
          {SESSION_STATUSES.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => setStatus(s.value)}
              className={`rounded-md border px-2 py-1.5 text-xs font-medium transition-colors ${
                status === s.value ? s.color : 'border-[#ececec] text-[#6b7280] hover:bg-[#fafafa]'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Title */}
      <div>
        <label className={labelClass}>Title *</label>
        <input
          required
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={
            sessionType === 'live_class' ? 'React Hooks — Live Class'
            : sessionType === 'office_hours' ? 'Office Hours — Week 3'
            : 'Recorded: State Management'
          }
          className={inputClass}
        />
      </div>

      {/* Date + Time */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>
            {sessionType === 'recorded' ? 'Recorded on' : 'Date'}
          </label>
          <DatePicker value={scheduledDate} onChange={setScheduledDate} />
        </div>
        <div>
          <label className={labelClass}>Time</label>
          <Select value={scheduledTime} onValueChange={setScheduledTime}>
            <SelectTrigger>
              <SelectValue placeholder="Select time…" />
            </SelectTrigger>
            <SelectContent>
              {TIME_SLOTS.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Duration */}
      <div>
        <label className={labelClass}>Duration (mins)</label>
        <input
          type="number"
          min="1"
          value={durationMins}
          onChange={(e) => setDurationMins(e.target.value)}
          placeholder="90"
          className={inputClass}
        />
      </div>

      {/* Meeting URL — only for live / office hours */}
      {showMeetingUrl && (
        <div>
          <label className={labelClass}>Meeting URL (Zoom / Meet)</label>
          <input
            type="url"
            value={meetingUrl}
            onChange={(e) => setMeetingUrl(e.target.value)}
            placeholder="https://zoom.us/j/..."
            className={inputClass}
          />
        </div>
      )}

      {/* Recording URL */}
      <div>
        <label className={labelClass}>Recording URL</label>
        <input
          type="url"
          value={recordingUrl}
          onChange={(e) => setRecordingUrl(e.target.value)}
          placeholder="https://drive.google.com/ or YouTube link…"
          className={inputClass}
        />
      </div>

      {/* Notes */}
      <div>
        <label className={labelClass}>Notes / agenda</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Session agenda, pre-reads, or notes for students…"
          className={inputClass}
          style={{ resize: 'vertical' }}
        />
      </div>

      {/* Attendance required */}
      {sessionType !== 'recorded' && (
        <label className="flex cursor-pointer items-center gap-2.5 text-sm">
          <input
            type="checkbox"
            checked={attendanceRequired}
            onChange={(e) => setAttendanceRequired(e.target.checked)}
            className="h-4 w-4 rounded border-[#ececec] accent-orange-500"
          />
          <span className="font-medium">Attendance required</span>
          <span className="text-[#6b7280] text-xs">(shown as mandatory on learner dashboard)</span>
        </label>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="rounded-md border border-[#ececec] px-4 py-2 text-sm hover:bg-[#fafafa] transition-colors">
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy || !title}
          className="rounded-md bg-[#ff5a1f] px-4 py-2 text-sm font-medium text-white disabled:opacity-50 transition-colors"
        >
          {busy ? 'Saving…' : initial ? 'Update session' : 'Add session'}
        </button>
      </div>
    </form>
  );
}
